import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Import components
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingFlow from './components/OnboardingFlow';
import TopUpPage from './pages/TopUpPage';

import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import CreateCampaignPage from './pages/CreateCampaignPage';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import TelegramOnlyScreen from './components/TelegramOnlyScreen';
import HelpButton from './components/HelpButton';

// Import hooks and stores
import { useAuthStore } from './store/authStore';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import apiService from './services/api';

// Create theme based on new color scheme
const createAppTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4d648d', // Primary button color
    },
    secondary: {
      main: '#283655', // Links and hover color
    },
    background: {
      default: '#FFFFFF',
      paper: '#d0e1f9',
    },
    text: {
      primary: '#1e1f26',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    button: { fontWeight: 700 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          textTransform: 'none',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#283655',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: '#d0e1f9',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          caretColor: 'black',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          caretColor: 'black',
        },
      },
    },
  },
});

function App() {
  const { isAuthenticated, isLoading, hasCompletedOnboarding, setTelegramWebApp, authenticate, completeOnboarding } = useAuthStore();
  const { webApp, isReady, isTelegramEnvironment, initData } = useTelegramWebApp();
  // Enable debug mode automatically in local dev, or via explicit flag
  const isDebugMode = (import.meta.env.VITE_DEBUG_MODE === 'true') || import.meta.env.DEV;

  // Set initDataProvider for API service - init_data будет добавляться к КАЖДОМУ запросу
  useEffect(() => {
    // Всегда устанавливаем provider, даже если initData пустой (для debug mode)
    apiService.setInitDataProvider(() => initData || null);
  }, [initData]);

  // Initialize Telegram WebApp
  useEffect(() => {
    if (webApp && isReady && isTelegramEnvironment) {
      setTelegramWebApp(webApp);
      
      // Expand the app to full height
      webApp.expand();
      
      // Set the app as ready
      webApp.ready();
      
      // Disable closing confirmation for better UX
      webApp.isClosingConfirmationEnabled = false;
    }
  }, [webApp, isReady, isTelegramEnvironment, setTelegramWebApp]);

  // Auto-authenticate once per session
  const authAttemptedRef = React.useRef(false);
  useEffect(() => {
    if (authAttemptedRef.current) return;
    if (isAuthenticated || isLoading) return;

    // Telegram environment path
    if (isReady && isTelegramEnvironment && initData) {
      authAttemptedRef.current = true;
      authenticate(initData).catch((error) => {
        console.error('Auto-authentication failed:', error);
        authAttemptedRef.current = false; // allow retry on manual action if needed
      });
      return;
    }

    // Debug browser path: call auth without init_data (backend debug bypass)
    if (isDebugMode && isReady && !isTelegramEnvironment) {
      authAttemptedRef.current = true;
      authenticate('')
        .catch((error) => {
          console.error('Debug authentication failed:', error);
        });
    }
  }, [isReady, isTelegramEnvironment, initData, isAuthenticated, isLoading, authenticate, isDebugMode]);

  // С init_data авторизацией периодический refresh не нужен - init_data валиден пока открыто приложение

  // Create theme based on Telegram color scheme
  const theme = React.useMemo(() => {
    return createAppTheme();
  }, []);

  // Show loading screen while initializing
  if (!isReady) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  // Show Telegram-only screen if not in Telegram environment
  if (!isTelegramEnvironment && !isDebugMode) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TelegramOnlyScreen />
      </ThemeProvider>
    );
  }

  // Show loading if still authenticating
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  // Show onboarding if user is authenticated but hasn't completed onboarding
  if (isAuthenticated && !hasCompletedOnboarding) {
    return (
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <OnboardingFlow onComplete={completeOnboarding} />
          <HelpButton />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/topup" element={<TopUpPage />} />

              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/campaigns/create" element={<CreateCampaignPage />} />
              <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </Router>
        <HelpButton />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 